import {Component, Input, OnChanges, SimpleChanges} from '@angular/core';
import {QlBuilderViolation} from "../../../../shared/ql/builder/ql-builder-violation";
import {UiQlWidgetPreviewState} from "../../../interface/ui-ql-widget-preview-state.interface";
import {UiQlWidgetPreviewStatus} from "../../../interface/ui-ql-widget-preview-status.interface";
import {UiQlWidgetPreviewPhase} from "../../../interface/ui-ql-widget-preview-phase.interface";
import {UiQlWidgetValidateResponseDto} from "../../../model/dto/api/ui-ql-widget-validate-response.dto";
import {uiQlViolationText} from "../../../logic/ui-ql-violation-texts";

/**
 * Playground tab "Inspektor": what was sent and what came back for the CURRENT definition —
 * the request (query + pipe, count on/off), the preview phase, runtime, row count or
 * "Anzahl nicht verfügbar" with its reason, pipe errors (HTTP 200) and the policy verdict of the
 * workbench's ONE validate call (`currentValidation$`: valid, envBound, violations in German).
 *
 * Pure display: every fact is handed in by the page (preview `stateChange` / `statusChange`, state
 * `currentValidation$`) — the inspector itself calls nothing.
 *
 * Declared in `UiQlWidgetBuilderModule` (NgModule, fe_core_standards) - its only host is
 * `UiQlWidgetWorkbenchPage` of the same module.
 */
@Component({
    selector: 'ui-ql-inspector',
    templateUrl: './ui-ql-inspector.component.html',
    styleUrls: ['./ui-ql-inspector.component.scss'],
    standalone: false,
})
export class UiQlInspectorComponent implements OnChanges {

    static readonly TEXT_COUNT_UNAVAILABLE = 'Anzahl nicht verfügbar';

    /** Last evaluation of the preview (`ui-ql-widget-preview` `stateChange`). */
    @Input() previewState: UiQlWidgetPreviewState | null = null;

    /** Last load of the preview widget (`statusChange`), `null` while none arrived for the current evaluation. */
    @Input() previewStatus: UiQlWidgetPreviewStatus | null = null;

    /** Validate answer for the current revision (`currentValidation$`), `null` while pending. */
    @Input() validation: UiQlWidgetValidateResponseDto | null = null;

    @Input() validating: boolean = false;

    /** German text of a technically failed validate call. */
    @Input() validationError: string | null = null;

    requestJson: string = '';

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['previewState']) {
            const request = this.previewState?.widget?.request;
            this.requestJson = request ? JSON.stringify(request, null, 2) : '';
        }
    }

    get phaseLabel(): string {
        return UiQlInspectorComponent.phaseText(this.previewState?.phase ?? 'empty');
    }

    /** "12 von 240 Zeilen" or "12 Zeilen · Anzahl nicht verfügbar"; `null` while nothing was loaded. */
    get countText(): string | null {
        return UiQlInspectorComponent.countText(this.previewState, this.previewStatus);
    }

    /** Reason shown next to "Anzahl nicht verfügbar", `null` when a count is available. */
    get countUnavailableReason(): string | null {
        const widget = this.previewState?.widget;
        return widget && !widget.countAvailable ? widget.countUnavailableReason : null;
    }

    violationText(violation: QlBuilderViolation): string {
        return uiQlViolationText(violation);
    }

    static countText(state: UiQlWidgetPreviewState | null, status: UiQlWidgetPreviewStatus | null): string | null {
        const widget = state?.widget;
        if (!widget || !status) {
            return null;
        }
        if (!widget.countAvailable) {
            return `${status.rows} Zeilen · ${UiQlInspectorComponent.TEXT_COUNT_UNAVAILABLE}`;
        }
        return `${status.rows} von ${status.total ?? '–'} Zeilen`;
    }

    static phaseText(phase: UiQlWidgetPreviewPhase): string {
        switch (phase) {
            case 'empty': return 'Keine Abfrage';
            case 'pending': return 'Wird geprüft …';
            case 'violations': return 'Nicht ausgeführt (Regelverstoß)';
            case 'invalid': return 'Nicht ausgeführt (Zuordnung ungültig)';
            case 'error': return 'Fehler';
            case 'ready': return 'Ausgeführt';
            default: return String(phase);
        }
    }
}
